import { clsx } from "clsx";
import Markdown from "react-markdown";
import rehypeMathjax from "rehype-mathjax";
import remarkMath from "remark-math";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { twMerge } from "tailwind-merge";
import DynamicRechartsChart from "../../components/specialized/DynamicRechartsChart";
import TableDownloadCard from "../../components/specialized/TableDownloadCard";

class Utils {
    truncateString(text, maxLength = 21) {
        if (text.length <= maxLength) {
            return text;
        }
        return text.slice(0, maxLength - 3) + "...";
    }

    formatDateToDDMMYYYY(dateString) {
        const date = new Date(dateString);

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();

        return `${day}/${month}/${year}`;
    }

    sanitizeMarkdown(text) {
        return text
            .replace(/\\\(/g, "$(")
            .replace(/\\\)/g, ")$")
            .replace(/\\\[/g, "$(")
            .replace(/\\\]/g, ")$")
            .replace(/\\n/g, '\n');
    }

    renderWithReports(text, chartsDict, tablesDict, threadId) {
        //console.log("Thread ID:", threadId);
        // The live chat keeps its charts and tables in dictionaries keyed by thread; a
        // scheduled answer carries its own lists (ScheduledFeed.js), and the feed still has a
        // thread id. A list is the answer's, whatever the thread.
        const pick = (source) => {
            if (!source) return [];
            if (Array.isArray(source)) return source;
            return threadId ? (source[threadId] || []) : [];
        };
        const charts = pick(chartsDict);
        const tables = pick(tablesDict);
        //console.log("Charts for thread:", charts);
        //console.log("Tables for thread:", tables);

        const parts = text.split(/(\{\{CHART:\d+\}\}|\{\{TABLE:\d+\}\})/g);

        //console.log("Parts after split:", parts);

        return parts.map((part, i) => {
            if (!part || part.trim() === '') {
                return null;
            }
            //console.log(`Processing part ${i}:`, part);

            // Check for CHART placeholder
            const chartMatch = part.match(/\{\{CHART:(\d+)\}\}/);
            if (chartMatch) {
                const index = Number(chartMatch[1]);
                let chartData = charts[index];
                console.log("Rendering chart:", chartData);
                return (
                    <DynamicRechartsChart
                        key={`chart-${i}`}
                        chartConfig={chartData}
                    />
                );
            }

            const tableMatch = part.match(/\{\{TABLE:(\d+)\}\}/);
            if (tableMatch) {
                const index = Number(tableMatch[1]);
                let tableData = tables[index];
                //console.log("Rendering table download link:", tableData);

                return (
                    <TableDownloadCard
                        key={`table-${i}`}
                        tableData={tableData}
                        index={index}
                    />
                );
            }

            return (
                <Markdown
                    key={`md-${i}`}
                    remarkPlugins={[remarkGfm, remarkMath]}
                    rehypePlugins={[rehypeMathjax, rehypeRaw]}
                    components={{
                        table: ({ children }) => (
                            <div className="table-wrapper">
                                <table>{children}</table>
                            </div>
                        ),
                        th: ({ children }) => <th>{children}</th>,
                        td: ({ children }) => <td>{children}</td>,
                    }}
                    className="prose max-w-none text-violet-100 text-[14px] font-notosans font-emoji"

                >
                    {this.sanitizeMarkdown(part)}
                </Markdown>
            );
        }).filter(Boolean);
    }

    cn(...inputs) {
        return twMerge(clsx(inputs));
    }

    getFileIcon(extension) {
        switch (extension) {
            case 'pdf':
                return <span className="flex justify-center items-center w-5 h-5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                        <path d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625z" />
                        <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
                    </svg>
                </span>;
            case 'csv':
                return <span className="flex justify-center items-center w-5 h-5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M1.5 5.625c0-1.036.84-1.875 1.875-1.875h17.25c1.035 0 1.875.84 1.875 1.875v12.75c0 1.035-.84 1.875-1.875 1.875H3.375A1.875 1.875 0 011.5 18.375V5.625zM21 9.375A.375.375 0 0020.625 9h-7.5a.375.375 0 00-.375.375v1.5c0 .207.168.375.375.375h7.5a.375.375 0 00.375-.375v-1.5zm0 3.75a.375.375 0 00-.375-.375h-7.5a.375.375 0 00-.375.375v1.5c0 .207.168.375.375.375h7.5a.375.375 0 00.375-.375v-1.5zm0 3.75a.375.375 0 00-.375-.375h-7.5a.375.375 0 00-.375.375v1.5c0 .207.168.375.375.375h7.5a.375.375 0 00.375-.375v-1.5zM10.875 18.75a.375.375 0 00.375-.375v-1.5a.375.375 0 00-.375-.375h-7.5a.375.375 0 00-.375.375v1.5c0 .207.168.375.375.375h7.5zM3.375 15h7.5a.375.375 0 00.375-.375v-1.5a.375.375 0 00-.375-.375h-7.5a.375.375 0 00-.375.375v1.5c0 .207.168.375.375.375zm0-3.75h7.5a.375.375 0 00.375-.375v-1.5A.375.375 0 0010.875 9h-7.5A.375.375 0 003 9.375v1.5c0 .207.168.375.375.375z" clipRule="evenodd" />
                    </svg>
                </span>;
            case 'json':
            case 'xml':
            case 'html':
                return <span className="flex justify-center items-center w-5 h-5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                        <path fillRule="evenodd" d="M14.447 3.027a.75.75 0 01.527.92l-4.5 16.5a.75.75 0 01-1.448-.394l4.5-16.5a.75.75 0 01.921-.526zM16.72 6.22a.75.75 0 011.06 0l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 11-1.06-1.06L21.44 12l-4.72-4.72a.75.75 0 010-1.06zm-9.44 0a.75.75 0 010 1.06L2.56 12l4.72 4.72a.75.75 0 11-1.06 1.06L.97 12.53a.75.75 0 010-1.06l5.25-5.25a.75.75 0 011.06 0z" clipRule="evenodd" />
                    </svg>
                </span>;
            default:
                return <span className="flex justify-center items-center w-5 h-5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4">
                        <path d="M5.625 1.5c-1.036 0-1.875.84-1.875 1.875v17.25c0 1.035.84 1.875 1.875 1.875h12.75c1.035 0 1.875-.84 1.875-1.875V12.75A3.75 3.75 0 0016.5 9h-1.875a1.875 1.875 0 01-1.875-1.875V5.25A3.75 3.75 0 009 1.5H5.625z" />
                        <path d="M12.971 1.816A5.23 5.23 0 0114.25 5.25v1.875c0 .207.168.375.375.375H16.5a5.23 5.23 0 013.434 1.279 9.768 9.768 0 00-6.963-6.963z" />
                    </svg>
                </span>;
        }
    }
}

const utils = new Utils();
export default utils;
